import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import http from 'helpers/requests';
import { DEFAULT_CERTIFICATE_PRICE } from 'modules/PublicCatalog/utils/certificatePrice';

dayjs.extend(utc);

// The certificate price the app boots with, from the freshest figure the EEX
// payload actually carries.
//
// EEX publishes once a week, on Friday, so `thisWeekPrice` is null for a stretch
// of EVERY week — and null indefinitely whenever the upstream API is unreachable
// or the local price table has not been back-filled. Anything that gates on
// having a price therefore cannot key off `thisWeekPrice` alone; that is exactly
// what wedged the declarant/operator overview in a permanent skeleton (see
// `usePageDataPrefetcher`). Walk back through what is present, then fall back to
// the shipped default so a price always exists.
export const resolveCarbonPrice = (eexData) => {
  const weekly = Array.isArray(eexData?.weeklyPrices) ? eexData.weeklyPrices : [];
  const usable = [
    eexData?.thisWeekPrice?.price,
    eexData?.lastWeekPrice?.price,
    // `weeklyPrices` is sorted ascending by date in `select`, so the tail is the
    // most recent week on record regardless of how far back it is.
    weekly[weekly.length - 1]?.price,
  ].find((price) => Number.isFinite(Number(price)) && Number(price) > 0);

  return usable != null ? Number(usable) : DEFAULT_CERTIFICATE_PRICE;
};

function average(values) {
  if (!values || values.length === 0) return 0;
  const sum = values.reduce((acc, v) => acc + Number(v || 0), 0);
  return sum / values.length;
}

function pctChange(current, previous) {
  if (!previous) return 0;
  return ((current - previous) / previous) * 100;
}

export function useEexAnnualPrices({ year, enabled = true }) {
  return useQuery({
    queryKey: ['eexAnnualPrices', year],
    queryFn: async () => {
      const { data } = await http.get('/eex/price/annual', { params: { year } });
      return data;
    },
    enabled,
    select: (api) => {
      const weekly = Array.isArray(api.weeklyPrices) ? api.weeklyPrices : [];
      const sorted = weekly.slice().sort((a, b) => (dayjs(a.date).isAfter(dayjs(b.date)) ? 1 : -1));

      const last = sorted[sorted.length - 1] || null;
      const first = sorted[0] || null;

      const prices = sorted.map((w) => Number(w.price || 0));
      const labels = sorted.map((w) => dayjs.utc(w.date).format('YYYY-MM-DD'));

      // Metrics. Both are null whenever the price table is empty for the range
      // — the backend returns them straight from `findHighestAndLowestPrices`,
      // which is `{ highest: null, lowest: null }` on no rows. Spreading null is
      // fine; reading `.tradeDate` off it threw, and a throw inside `select`
      // fails the whole query, so an empty table took down every consumer of
      // this hook rather than just the two figures it actually feeds. Every
      // caller already optional-chains `metrics.highest`.
      const highest = api.highest ? { ...api.highest, date: api.highest.tradeDate } : null;
      const lowest = api.lowest ? { ...api.lowest, date: api.lowest.tradeDate } : null;

      const avg30d = average(prices.slice(-4)); // ~4 weeks ~ 30 days
      const historicalAvg = average(prices);
      const ytdChangePct = last && first ? pctChange(last.price, first.price) : 0;

      return {
        year: api.year,
        productRoot: api.productRoot,
        weeklyPrices: sorted, // [{ week, date, price }]
        thisWeekPrice: api.thisWeekPrice,
        lastWeekPrice: api.lastWeekPrice,
        metrics: {
          thisWeekPrice: api.thisWeekPrice,
          highest, // { price, date }
          lowest, // { price, date }
          avg30d: Number(avg30d.toFixed(2)),
          historicalAvg: Number(historicalAvg.toFixed(2)),
          ytdChangePct: Number(ytdChangePct.toFixed(2)),
        },
        chart: {
          labels, // string[]
          series: prices, // number[]
        },
        quarterPriceStatuses: Object.fromEntries(
          Object.entries(api.quarterPriceStatuses || {}).filter(
            ([, entry]) => entry?.year === year,
          ),
        ),
        raw: api,
      };
    },
    staleTime: 60 * 60 * 1000, // 1h
    gcTime: 2 * 60 * 60 * 1000,
    retry: 1,
  });
}
